import { Box, Typography, useMediaQuery } from '@mui/material';
import {
  getLocalStorage,
  getTeacherSubjectEnum,
  ImageWrapper,
  IStyles,
  Loader,
  NumericalInputField,
  pxToRem,
  pxTovW,
  SelectMenu,
  theme,
  useCommonServiceClientContext,
} from '@geneo2-web/shared-ui';
import {
  useAppDispatch,
  useAppSelector,
} from 'apps/teacher/src/reduxStore/reduxHooks';
import { ChangeEvent, useEffect, useState } from 'react';
import {
  setQPGFormatDetails,
  setQPGGeneratedTemplates,
} from '../../Assessment/reducer/assessmentFlow.slice';
import {
  MultiSubjectSection,
  MultiSubjectSectionStatusEnum,
  TaskCreationStatusEnum,
  TaskEnum,
} from '@protos/learning_management/lms.db_pb';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { QPG_ASSESSMENT_PUBLISH } from 'apps/teacher/src/routeHandling/RoutesNomenclature';
import { setToastInfoV2 } from '../../Home/reducer/homeDashboard.slice';
import { ConnectError } from '@bufbuild/connect';
import {
  buildTaskInfoModels,
  convertToMultiSubjectFormateInfo,
} from '../../Assessment/QuestionPaperGeneratorV2/QPGv2.utils';
import { Subject_SubjectEnum } from '@protos/common/common.db_pb';
import { DifficultyLevelEnum } from '@protos/content_management/content.db_pb';
import { SelectMultiSubjectsPopup } from './SelectMultiSubjectsPopup';
import { getSubjectsForClass } from '../../../utils/functions';

const styles: IStyles = {
  root: {},
  lato27: {
    fontFamily: 'Lato',
    fontSize: { xs: pxToRem(15), md: pxTovW(27) },
  },
  subjectSelectBox: {
    borderRadius: { xs: pxToRem(10), md: pxTovW(30) },
    border: '1px solid #D2D2D2',
    paddingX: { xs: pxToRem(10), md: pxTovW(30) },
    paddingTop: { xs: pxToRem(10), md: pxTovW(15) },
    paddingBottom: { xs: pxToRem(10), md: pxTovW(15) },
    marginBottom: { xs: pxToRem(10), md: pxTovW(20) },
  },
  deleteSmall: {
    height: { xs: pxToRem(15), md: pxTovW(20) },
    width: { xs: pxToRem(15), md: pxTovW(20) },
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'flex-end',
    marginLeft: 'auto',
  },
  numericalInputField: {
    width: '100%',
    marginTop: { xs: pxToRem(10), md: pxTovW(18) },
    '& input': {
      width: '100%',
      height: { xs: pxToRem(40), md: pxTovW(74) },
      textAlign: 'center',
      borderRadius: { xs: pxToRem(15), md: pxTovW(15) },
    },
  },
  templateSelectMenu: {
    width: '100%',
    marginTop: { xs: pxToRem(10), md: pxTovW(18) },
    height: { xs: pxToRem(40), md: pxTovW(60) },
  },
};

interface IProps {
  open: boolean;
  handleClose: () => void;
  onProceedClick?: () => void;
  sectionMetaInfo?: MultiSubjectSection[];
}
export const QPGSelectMultiSubjectsPopup = (props: IProps) => {
  const { open, handleClose, onProceedClick, sectionMetaInfo } = props;

  const [searchParams] = useSearchParams();
  const assessmentId = searchParams.get('assessment_id');
  const teacherId = getLocalStorage('userId');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {
    LmsAssessmentTeacherAPIServiceV6WithStatusCodeHandler,
    LmsAssessmentTeacherAPIServiceV2WithStatusCodeHandler,
  } = useCommonServiceClientContext();
  const { user_info } = useAppSelector((state) => state.auth);
  const { class_subject_info } = useAppSelector((state) => state.homeDashboard);
  const { qpg_format_details, generated_qpg_templates, qpg_content } =
    useAppSelector((state) => state.assessment);

  const [loading, setLoading] = useState<'loading' | 'success' | 'error'>(
    'success'
  );
  const [classList, setClassList] = useState<{ name: string; id: number }[]>(
    []
  );
  const [radioValue, setRadioValue] = useState<'manual' | 'template'>('manual');
  const [modifiedMetaInfo, setModifiedMetaInfo] = useState<
    MultiSubjectSection[]
  >(sectionMetaInfo ?? []);

  // ----------------------------------------------------

  const popupCloseHandler = () => {
    handleClose();
    setRadioValue('manual');
    dispatch(
      setQPGFormatDetails({
        selectedSubjectsAndMarks: [
          { subject: { name: '', id: 0 }, marks: undefined },
        ],
        selectedTemplates: [{ name: '', id: 0 }],
      })
    );
  };

  // ----------------------------------------------------

  const getClassNamesAndClassIds = () => {
    const uniqueClassesMap = new Map();

    user_info?.teachClassSubjects?.forEach((classData) => {
      if (!uniqueClassesMap.has(classData.classId)) {
        uniqueClassesMap.set(classData.classId, {
          name: `Class ${classData.className}`,
          id: classData.classId,
        });
      }
    });

    return Array.from(uniqueClassesMap.values()).sort(
      (a, b) => Number(a.name.split(' ')[1]) - Number(b.name.split(' ')[1])
    );
  };

  // ----------------------------------------------------

  const handleClassChange = (value: string) => {
    const classData = classList.find((classData) => classData.name === value);
    dispatch(
      setQPGFormatDetails({
        ...qpg_format_details,
        selectedClass: classData,
        selectedSubjectsAndMarks: [
          { subject: { name: '', id: 0 }, marks: undefined },
        ],
        selectedTemplates: [{ name: '', id: 0 }],
      })
    );
  };

  // ----------------------------------------------------

  const controlRadioProps = (item: string) => ({
    name: 'color-radio-button',
    value: item,
    checked: radioValue === item,
    onChange: async (event: ChangeEvent<HTMLInputElement>) => {
      setRadioValue(event.target.value as 'manual' | 'template');
      if (event.target.value === 'template') {
        dispatch(
          setQPGFormatDetails({
            ...qpg_format_details,
            selectedTemplates: [{ name: '', id: 0 }],
          })
        );
      } else {
        dispatch(
          setQPGFormatDetails({
            ...qpg_format_details,
            selectedSubjectsAndMarks: [
              { subject: { name: '', id: 0 }, marks: undefined },
            ],
          })
        );
      }
    },
    // inputProps: { 'aria-label': item },
  });

  // ----------------------------------------------------

  const handleSubjectChange = (name: string, id: number, index: number) => {
    const updatedSubjectsAndMarks = [
      ...(qpg_format_details?.selectedSubjectsAndMarks ?? []),
    ];
    updatedSubjectsAndMarks[index] = {
      ...updatedSubjectsAndMarks[index],
      subject: { name, id },
    };
    dispatch(
      setQPGFormatDetails({
        ...qpg_format_details,
        selectedSubjectsAndMarks: updatedSubjectsAndMarks,
      })
    );
    const subjectEnum =
      getTeacherSubjectEnum(id, user_info?.teachClassSubjects) ??
      Subject_SubjectEnum.UNDEFINED;
    const updatedMetaInfo = [...modifiedMetaInfo];
    const existing = updatedMetaInfo[index];
    updatedMetaInfo[index] = new MultiSubjectSection({
      ...existing,
      subjectId: id,
      subject: subjectEnum,
    });
    setModifiedMetaInfo(updatedMetaInfo);
  };

  // ----------------------------------------------------

  const handleMarksChange = (marks: number, index: number) => {
    if (marks < 0) return;

    if (marks > 300) marks = 300;

    const updatedSubjectsAndMarks = [
      ...(qpg_format_details?.selectedSubjectsAndMarks ?? []),
    ];
    updatedSubjectsAndMarks[index] = {
      ...updatedSubjectsAndMarks[index],
      marks: marks === 0 ? undefined : marks,
    };
    dispatch(
      setQPGFormatDetails({
        ...qpg_format_details,
        selectedSubjectsAndMarks: updatedSubjectsAndMarks,
      })
    );

    const updatedMetaInfo = [...modifiedMetaInfo];
    const existing = updatedMetaInfo[index];
    updatedMetaInfo[index] = new MultiSubjectSection({
      ...existing,
      preferredMarks: marks === 0 ? undefined : marks,
    });
    setModifiedMetaInfo(updatedMetaInfo);
  };

  // ----------------------------------------------------

  const isSectionRowLocked = (index: number) => {
    if (!sectionMetaInfo?.length) return false;
    const status = modifiedMetaInfo[index]?.multiSubjectSectionStatus;
    return (
      status !== undefined &&
      status !==
        MultiSubjectSectionStatusEnum.MULTI_SUBJECT_SECTION_STATUS_UNDEFINED &&
      status !==
        MultiSubjectSectionStatusEnum.MULTI_SUBJECT_SECTION_STATUS_INITIALIZED
    );
  };

  // ----------------------------------------------------

  const idAddMoreDisabled = () => {
    const selectedSubjectsAndMarks =
      qpg_format_details?.selectedSubjectsAndMarks ?? [];
    if (radioValue === 'manual') {
      return (
        selectedSubjectsAndMarks[selectedSubjectsAndMarks.length - 1]?.subject
          ?.id === 0 || selectedSubjectsAndMarks.length >= 20
      );
    } else {
      return (
        qpg_format_details?.selectedTemplates?.length ===
          generated_qpg_templates?.length ||
        (qpg_format_details?.selectedTemplates?.length ?? 0) >= 20
      );
    }
  };

  // ----------------------------------------------------

  const handleAddMore = () => {
    if (idAddMoreDisabled()) return;
    const selectedSubjectsAndMarks =
      qpg_format_details?.selectedSubjectsAndMarks ?? [];
    if (radioValue === 'manual') {
      const lastItem =
        selectedSubjectsAndMarks[selectedSubjectsAndMarks.length - 1];

      if (!lastItem?.subject?.id) return;

      dispatch(
        setQPGFormatDetails({
          ...qpg_format_details,
          selectedSubjectsAndMarks: [
            ...selectedSubjectsAndMarks,
            { subject: { name: '', id: 0 }, marks: undefined },
          ],
        })
      );

      const newSectionMetaInfo = {
        // subjectId: 0,
        // subject: Subject_SubjectEnum.UNDEFINED,
        // sectionName: '',
        // sectionTitle: '',
        instructions: [],
        // numberOfQuestion: 0,
        // sectionNo: modifiedMetaInfo.length + 1,
        // preferredMarks: 0,
        avgDifficultyLevel: DifficultyLevelEnum.DIFFICULTY_LEVEL_UNDEFINED,
        multiSubjectSubSections: [],
        multiSubjectSectionStatus:
          MultiSubjectSectionStatusEnum.MULTI_SUBJECT_SECTION_STATUS_INITIALIZED,
      };
      const updatedMetaInfo = [
        ...modifiedMetaInfo,
        new MultiSubjectSection(newSectionMetaInfo),
      ].map(
        (item, index) =>
          new MultiSubjectSection({
            ...item,
            sectionNo: index + 1,
            multiSubjectSectionStatus: item.multiSubjectSectionStatus
              ? item.multiSubjectSectionStatus
              : MultiSubjectSectionStatusEnum.MULTI_SUBJECT_SECTION_STATUS_INITIALIZED,
          })
      );

      setModifiedMetaInfo(updatedMetaInfo);
    } else {
      //  TEMPLATE
      dispatch(
        setQPGFormatDetails({
          ...qpg_format_details,
          selectedTemplates: [
            ...(qpg_format_details?.selectedTemplates ?? []),
            { name: '', id: 0 },
          ],
        })
      );
    }
  };

  // ----------------------------------------------------

  const handleRemoveSection = (deleteIndex: number) => {
    let updatedSubjectsAndMarks =
      qpg_format_details?.selectedSubjectsAndMarks?.filter(
        (item, index) => index !== deleteIndex
      );
    if (updatedSubjectsAndMarks?.length === 0)
      updatedSubjectsAndMarks = [
        { subject: { name: '', id: 0 }, marks: undefined },
      ];
    dispatch(
      setQPGFormatDetails({
        ...qpg_format_details,
        selectedSubjectsAndMarks: updatedSubjectsAndMarks,
      })
    );
    const updatedMetaInfo = modifiedMetaInfo
      .filter((_, index) => index !== deleteIndex)
      .map(
        (item, index) =>
          new MultiSubjectSection({ ...item, sectionNo: index + 1 })
      );
    setModifiedMetaInfo(updatedMetaInfo);
  };

  // ----------------------------------------------------

  const getTemplateList = (
    currentIndex: number
  ): { name: string; id: number }[] => {
    if (!generated_qpg_templates?.length) return [];
    const selectedTemplates = qpg_format_details?.selectedTemplates ?? [];
    const selectedIdsElsewhere = selectedTemplates
      .map((t, i) => (i !== currentIndex && t?.id ? t.id : null))
      .filter((id): id is number => id !== null);
    const selectedIdSet = new Set(selectedIdsElsewhere);
    return generated_qpg_templates.map((template) => ({
      name: template.title,
      id: template.questionPaperTemplateId,
    }));
    // .filter((item) => !selectedIdSet.has(item.id));
  };

  // ----------------------------------------------------

  const handleTemplateSubjectChange = (subjectValue: string, index: number) => {
    const subjectId = getSubjectsForClass({
      classId: qpg_format_details?.selectedClass?.id,
      userInfo: user_info,
    }).find((subject) => subject.name === subjectValue)?.id;
    if (!subjectId) return;
    const subject = { name: subjectValue, id: subjectId };
    const updatedTemplates = [...(qpg_format_details?.selectedTemplates ?? [])];
    updatedTemplates[index] = { ...updatedTemplates[index], subject };
    dispatch(
      setQPGFormatDetails({
        ...qpg_format_details,
        selectedTemplates: updatedTemplates,
      })
    );
  };

  // ----------------------------------------------------

  const handleTemplateChange = (value: string, index: number) => {
    const templateData = getTemplateList(index).find(
      (item) => item.name === value
    );
    if (!templateData) return;
    const updatedTemplates = [...(qpg_format_details?.selectedTemplates ?? [])];
    updatedTemplates[index] = {
      ...updatedTemplates[index],
      ...templateData,
    };
    dispatch(
      setQPGFormatDetails({
        ...qpg_format_details,
        selectedTemplates: updatedTemplates,
      })
    );
  };

  // ----------------------------------------------------

  const handleRemoveTemplate = (deleteIndex: number) => {
    let updatedTemplates = qpg_format_details?.selectedTemplates?.filter(
      (item, index) => index !== deleteIndex
    );
    if (updatedTemplates?.length === 0)
      updatedTemplates = [{ name: '', id: 0 }];
    dispatch(
      setQPGFormatDetails({
        ...qpg_format_details,
        selectedTemplates: updatedTemplates,
      })
    );
  };

  // ----------------------------------------------------

  const isProceedButtonDisabled = () => {
    if (radioValue === 'manual') {
      return (
        !qpg_format_details?.selectedClass ||
        !qpg_format_details?.assessmentName?.trim() ||
        qpg_format_details?.selectedSubjectsAndMarks?.[0]?.subject?.id === 0 ||
        qpg_format_details?.selectedSubjectsAndMarks?.some(
          (item) => item.marks === undefined || item.marks <= 0
        )
      );
    } else {
      return (
        !qpg_format_details?.selectedClass ||
        !qpg_format_details?.assessmentName ||
        qpg_format_details?.selectedTemplates?.[0]?.id === 0
      );
    }
  };

  // ----------------------------------------------------

  const getSubjectIds = (): number[] | undefined => {
    if (radioValue === 'manual') {
      return qpg_format_details?.selectedSubjectsAndMarks
        ?.map((item) => item.subject?.id)
        ?.filter((item): item is number => item !== undefined);
    } else {
      return qpg_format_details?.selectedTemplates
        ?.filter((template) => template.id !== 0)
        ?.map((item) => item.subject?.id)
        ?.filter((item): item is number => item !== undefined);
    }
  };

  // ----------------------------------------------------

  const handleProceedClick = async () => {
    let multiSubjectFormateInfo;
    if (radioValue === 'manual') {
      // MANUAL
      multiSubjectFormateInfo = convertToMultiSubjectFormateInfo(
        qpg_format_details?.selectedSubjectsAndMarks ?? [],
        user_info?.teachClassSubjects
      );
      multiSubjectFormateInfo = multiSubjectFormateInfo.map(
        (item, index) =>
          new MultiSubjectSection({ ...item, sectionNo: index + 1 })
      );
    } else {
      // TEMPLATE
      multiSubjectFormateInfo = qpg_format_details?.selectedTemplates
        ?.filter((template) => template.id !== 0)
        ?.map((template, index) => {
          const subjectEnum =
            getTeacherSubjectEnum(
              template.subject?.id,
              user_info?.teachClassSubjects
            ) ?? Subject_SubjectEnum.UNDEFINED;

          const templateMarks = generated_qpg_templates?.find(
            (templateData) =>
              templateData.questionPaperTemplateId === template.id
          )?.multiSubjectSectionTemplate?.marks;

          return new MultiSubjectSection({
            subject: subjectEnum,
            subjectId: template.subject?.id,
            preferredMarks: templateMarks,
            sectionNo: index + 1,
            templateId: template.id,
          });
        }
      );
    }

    try {
      setLoading('loading');
      const assessmentCreationSubmitResponse =
        await LmsAssessmentTeacherAPIServiceV6WithStatusCodeHandler.assessmentCreationSubmit(
          {
            teacherId: BigInt(teacherId),
            assessmentId: Number(assessmentId),
            classId: qpg_format_details?.selectedClass?.id,
            creationStatus: TaskCreationStatusEnum.TASK_CREATION_STATUS_DRAFT,
            multiSubjectFormateInfo: sectionMetaInfo
              ? undefined
              : multiSubjectFormateInfo,
            multiSubjectSectionMetaInfo: sectionMetaInfo
              ? modifiedMetaInfo
              : undefined,
            sectionId: class_subject_info?.sectionId,
            subjectIds: getSubjectIds(),
            taskName: qpg_format_details?.assessmentName,
            taskType: TaskEnum.TASK_MULTI_SUBJECT_QUESTION_PAPER_CUSTOM,
            instructions: sectionMetaInfo
              ? qpg_content?.assessment?.assessmentInstructions
              : undefined,
            questionModuleMap:
              qpg_content?.questionsSequenceInfo?.questionModuleMap,
            selectedTasksInfo: sectionMetaInfo
              ? buildTaskInfoModels(modifiedMetaInfo)
              : undefined,
            chapterIdsInfo:
              qpg_content?.assessment?.taskMetaInfo?.chapterInfo
                ?.chapterTopicsInfo,
          }
        );

      if (assessmentCreationSubmitResponse.status === 200) {
        navigate(
          `${QPG_ASSESSMENT_PUBLISH}?assessment_id=${assessmentCreationSubmitResponse.data?.assessment?.assessmentId}`
        );
        onProceedClick?.();
        setLoading('success');
      } else {
        dispatch(
          setToastInfoV2({
            label: 'Error while creating assessment',
            variant: 'error',
            open: true,
          })
        );
        setLoading('error');
      }
    } catch (error) {
      console.log('error:', error);
      setLoading('error');
      let message = 'Error while creating assessment';
      if (error instanceof ConnectError) {
        message = error.rawMessage?.replace(/.*\]\s*/, '').trim();
      }
      dispatch(
        setToastInfoV2({
          label: message,
          variant: 'error',
          open: true,
        })
      );
    }
  };

  // ----------------------------------------------------

  const fetchSectionTemplates = async () => {
    try {
      setLoading('loading');
      const response =
        await LmsAssessmentTeacherAPIServiceV2WithStatusCodeHandler.getQuestionPaperTemplate(
          {
            personId: BigInt(teacherId),
            taskType: TaskEnum.TASK_MULTI_SUBJECT_QUESTION_PAPER_CUSTOM,
          }
        );
      if (response.questionPaperTemplates) {
        dispatch(setQPGGeneratedTemplates(response.questionPaperTemplates));
      }
    } catch (error) {
      setLoading('error');
      dispatch(
        setToastInfoV2({
          label: 'Error while fetching section templates',
          variant: 'error',
          open: true,
        })
      );
    } finally {
      setLoading('success');
    }
  };

  // ----------------------------------------------------

  useEffect(() => {
    setClassList(getClassNamesAndClassIds());
  }, [user_info?.teachClassSubjects]);

  useEffect(() => {
    if (class_subject_info) {
      // Reset when unset.
      const hasValidSubjects =
        (qpg_format_details?.selectedSubjectsAndMarks?.length ?? 0) > 0 &&
        qpg_format_details?.selectedSubjectsAndMarks?.some(
          (s) => s.subject?.id != null && s.subject.id !== 0
        );
      if (!hasValidSubjects) {
        dispatch(
          setQPGFormatDetails({
            ...qpg_format_details,
            selectedClass: {
              id: class_subject_info.classId,
              name: `Class ${class_subject_info.classname}`,
            },
            selectedSubjectsAndMarks: [
              { subject: { name: '', id: 0 }, marks: undefined },
            ],
            selectedTemplates: [{ name: '', id: 0 }],
          })
        );
      }
    }
  }, [JSON.stringify(class_subject_info)]);

  useEffect(() => {
    fetchSectionTemplates();
  }, []);

  // ----------------------------------------------------

  if (loading === 'loading') return <Loader />;

  return (
    <SelectMultiSubjectsPopup
      popupOpen={open}
      popupCloseHandler={popupCloseHandler}
      popupTitle="Choose Subject & Paper Format"
      chooseClassData={{
        selectedClass: qpg_format_details?.selectedClass?.name ?? '',
        classList: classList,
        handleClassChange: handleClassChange,
        disabled: !!sectionMetaInfo,
      }}
      radioData={
        sectionMetaInfo
          ? []
          : [
              { title: 'Select Manually', value: 'manual', controlRadioProps },
              {
                title: 'Select Template',
                value: 'template',
                controlRadioProps,
              },
            ]
      }
      nameChangeData={{
        title: 'Add Question Paper Name',
        name: 'assessmentName',
        value: qpg_format_details?.assessmentName ?? '',
        onChange: (e: ChangeEvent<HTMLInputElement>) => {
          dispatch(
            setQPGFormatDetails({
              ...qpg_format_details,
              assessmentName: e.target.value,
            })
          );
        },
      }}
      sectionInputNode={
        <>
          {radioValue === 'manual' ? (
            <Box>
              {qpg_format_details?.selectedSubjectsAndMarks?.map(
                (item, index) => (
                  <Box key={index} sx={styles.subjectSelectBox}>
                    {!isSectionRowLocked(index) &&
                      !(
                        item.subject?.name === '' && item.marks === undefined
                      ) && (
                        <ImageWrapper
                          name="delete-small"
                          parentFolder="icons"
                          type="png"
                          onClick={() => {
                            handleRemoveSection(index);
                          }}
                          styles={styles.deleteSmall}
                        />
                      )}

                    <Box
                      sx={{
                        display: 'flex',
                        gap: { xs: pxToRem(10), md: pxTovW(20) },
                      }}
                    >
                      <Box sx={{ width: { xs: '60%', md: '75%' } }}>
                        <Typography variant="bodyText" sx={styles.lato27}>
                          Choose Subject
                        </Typography>
                        <SelectMenu
                          disabled={
                            !qpg_format_details?.selectedClass ||
                            isSectionRowLocked(index)
                          }
                          value={item.subject?.name ?? ''}
                          optionList={getSubjectsForClass({
                            classId: qpg_format_details?.selectedClass?.id,
                            userInfo: user_info,
                          })}
                          onChange={(subjectValue: string) => {
                            const subjectId = getSubjectsForClass({
                              classId: qpg_format_details?.selectedClass?.id,
                              userInfo: user_info,
                            }).find(
                              (subject) => subject.name === subjectValue
                            )?.id;
                            if (!subjectId) return;
                            handleSubjectChange(subjectValue, subjectId, index);
                          }}
                          menuPaperProps={{
                            minWidth: '100px !important',
                            width: { xs: '90%', md: pxTovW(700) },
                            maxWidth: '100%',
                          }}
                          menuProps={{
                            anchorOrigin: {
                              vertical: 'bottom',
                              horizontal: 'left',
                            },
                            transformOrigin: {
                              vertical: 'top',
                              horizontal: 'left',
                            },
                          }}
                          rootStyles={{
                            width: '100%',
                            marginTop: {
                              xs: pxToRem(10),
                              md: pxTovW(18),
                            },
                            height: {
                              xs: pxToRem(40),
                              md: pxTovW(60),
                            },
                          }}
                        />
                      </Box>

                      <Box sx={{ width: { xs: '38%', md: '20%' } }}>
                        <Typography variant="bodyText" sx={styles.lato27}>
                          Add Marks
                        </Typography>
                        <NumericalInputField
                          variant="outlined"
                          disabled={!item.subject?.id}
                          value={item.marks ?? ''}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            if (!item.subject?.id) return;
                            handleMarksChange(Number(e.target.value), index);
                          }}
                          sx={{
                            ...styles.numericalInputField,
                            '& .MuiOutlinedInput-root': {
                              fontFamily: 'Lato',
                              fontSize: {
                                xs: pxToRem(14),
                                md: pxTovW(27),
                              },
                              padding: '0',
                              border: `${pxTovW(1)} solid #A8D3FC`,
                              borderRadius: {
                                xs: pxToRem(8),
                                md: pxTovW(8),
                              },
                              height: {
                                xs: pxToRem(40),
                                md: pxTovW(60),
                              },
                              borderColor: !item.subject?.id
                                ? 'darkgray'
                                : '#A8D3FC',
                              backgroundColor: !item.subject?.id
                                ? 'lightgray'
                                : '#DBF5FF',
                            },
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                )
              )}
            </Box>
          ) : (
            <Box>
              {qpg_format_details?.selectedTemplates?.map((template, index) => (
                <Box key={index} sx={styles.subjectSelectBox}>
                  {template?.name !== '' && (
                    <ImageWrapper
                      name="delete-small"
                      parentFolder="icons"
                      type="png"
                      onClick={() => {
                        handleRemoveTemplate(index);
                      }}
                      styles={styles.deleteSmall}
                    />
                  )}
                  <Box
                    sx={{
                      display: 'flex',
                      gap: { xs: pxToRem(10), md: pxTovW(20) },
                    }}
                  >
                    <Box sx={{ width: { xs: '40%', md: '35%' } }}>
                      <Typography variant="bodyText" sx={styles.lato27}>
                        Choose Subject
                      </Typography>
                      <SelectMenu
                        disabled={!qpg_format_details?.selectedClass}
                        value={template.subject?.name ?? ''}
                        optionList={getSubjectsForClass({
                          classId: qpg_format_details?.selectedClass?.id,
                          userInfo: user_info,
                        })}
                        onChange={(subjectValue: string) =>
                          handleTemplateSubjectChange(subjectValue, index)
                        }
                        menuPaperProps={{
                          minWidth: '100px !important',
                          width: { xs: '60%', md: pxTovW(300) },
                          maxWidth: '100%',
                        }}
                        menuProps={{
                          anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left',
                          },
                          transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left',
                          },
                        }}
                        rootStyles={styles.templateSelectMenu}
                      />
                    </Box>
                    <Box sx={{ width: { xs: '60%', md: '60%' } }}>
                      <Typography variant="bodyText" sx={styles.lato27}>
                        Choose Template
                      </Typography>
                      <SelectMenu
                        disabled={!qpg_format_details?.selectedClass}
                        value={template.name ?? ''}
                        optionList={getTemplateList(index)}
                        onChange={(value: string) =>
                          handleTemplateChange(value, index)
                        }
                        menuPaperProps={{
                          minWidth: '100px !important',
                          width: { xs: '50%', md: pxTovW(600) },
                          maxWidth: '100%',
                        }}
                        menuProps={{
                          anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left',
                          },
                          transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left',
                          },
                        }}
                        rootStyles={styles.templateSelectMenu}
                      />
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </>
      }
      addMoreSectionData={{
        handleAddMore: handleAddMore,
        idAddMoreDisabled: idAddMoreDisabled(),
      }}
      proceedButtonData={{
        onClick: handleProceedClick,
        disabled: isProceedButtonDisabled() ?? false,
      }}
    />
  );
};
